import { useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import {
  useGetResourcesPaginated,
  useGetCountyOptionsWithState,
} from '@dispatch/shared/hooks';
import { MultiSelectOption, AttributeOptions } from '@dispatch/shared/types';
import { Filter } from '@dispatch/web/ui';

/**
 * Filter options lookup containing all static filter options
 */
interface FilterOptionsLookup {
  teamOptions: MultiSelectOption[];
  officeOptions: MultiSelectOption[];
  groupOptions: MultiSelectOption[];
  regionOptions: MultiSelectOption[];
  perilOptions: MultiSelectOption[];
  methodOfInspectionOptions: MultiSelectOption[];
  statesOptions: MultiSelectOption[];
  companyRoles: MultiSelectOption[];
  attributeGroupOptions?: MultiSelectOption[];
}

/**
 * Company attributes for dynamic resource filters
 */
interface CompanyAttributesConfig {
  companyAttributes?: AttributeOptions[];
}

interface UseSchedulerFiltersOptions {
  /** Lookup object with all static filter options */
  filterOptionsLookup: FilterOptionsLookup;
  /** Company attributes for dynamic resource filters */
  companyAttributes?: CompanyAttributesConfig;
  /** Task attribute options for the Task Attributes filter (derived from company attributeDefinition) */
  taskAttributeOptions?: MultiSelectOption[];
  /** Whether the current user is an adjuster (limits filter visibility) */
  isAdjuster?: boolean;
  /** Count of unassigned tasks for badge display */
  unassignedTasksCount?: number;
  /** Feature flag for qualified resources FNOL */
  qualifiedResourcesFnol?: boolean;
}

/**
 * Static task filter definitions that don't depend on runtime data
 */
const STATIC_TASK_FILTERS: Record<string, Filter> = {
  hasCatCode: {
    type: 'toggle',
    id: 'hasCatCode',
    name: 'hasCatCode',
    label: 'CAT Code Status',
    chipLabel: [
      { label: 'Has CAT Code', value: 'true' },
      { label: 'No CAT Code', value: 'false' },
    ],
    buttons: [
      { label: 'Has CAT Code', value: true },
      { label: 'No CAT Code', value: false },
    ],
  },
  initialOrSupplement: {
    id: 'initialOrSupplement',
    name: 'initialOrSupplement',
    label: 'Assignment Category',
    type: 'toggle',
    chipLabel: [
      { label: 'Initial', value: 'initial' },
      { label: 'Supplement', value: 'supplement' },
    ],
    buttons: [
      { value: 'initial', label: 'Initial' },
      { value: 'supplement', label: 'Supplement' },
    ],
  },
  lossDate: {
    id: 'lossDate',
    name: 'lossDate',
    label: 'Loss Date',
    start: 'lossDateFrom',
    end: 'lossDateTo',
    type: 'date-range',
  },
  appointmentDate: {
    id: 'appointmentDate',
    name: 'appointmentDate',
    label: 'Appointment Date',
    type: 'date-range',
    start: 'appointmentDateFrom',
    end: 'appointmentDateTo',
  },
  catCode: {
    id: 'catCode',
    name: 'catCode',
    label: 'CAT Code',
    type: 'text',
  },
  queueName: {
    id: 'queueName',
    name: 'queueName',
    label: 'Assignment Status',
    type: 'select',
    multi: true,
    options: [
      { value: 'auto_dispatch_q', title: 'Processing' },
      { value: 'manual_dispatch_q', title: 'Needs Dispatching' },
      { value: 'dispatched_q', title: 'Dispatched' },
      { value: 'needs_attention_q', title: 'Needs Attention' },
      { value: 'wip_q', title: 'Started' },
      { value: 'egress', title: 'Completed' },
      { value: 'cancelled_q', title: 'Cancelled' },
    ],
  },
  noticeDate: {
    id: 'noticeDate',
    name: 'noticeDate',
    label: 'Notice Date',
    type: 'date-range',
    start: 'noticeDateFrom',
    end: 'noticeDateTo',
  },
  receivedDate: {
    id: 'receivedDate',
    name: 'receivedDate',
    label: 'Received Date',
    start: 'receivedDateFrom',
    end: 'receivedDateTo',
    type: 'date-range',
  },
  lossZipCodes: {
    id: 'lossZipCodes',
    name: 'lossZipCodes',
    label: 'Loss Zip Code',
    type: 'text',
  },
};

/** Ordered list of core task filters that appear before dynamic/conditional filters */
const CORE_TASK_FILTERS: Filter[] = [
  STATIC_TASK_FILTERS['hasCatCode'],
  STATIC_TASK_FILTERS['initialOrSupplement'],
  STATIC_TASK_FILTERS['lossDate'],
  STATIC_TASK_FILTERS['appointmentDate'],
  STATIC_TASK_FILTERS['noticeDate'],
  STATIC_TASK_FILTERS['receivedDate'],
  STATIC_TASK_FILTERS['catCode'],
  STATIC_TASK_FILTERS['queueName'],
];

/**
 * Static resource filter definitions
 */
const STATIC_RESOURCE_FILTERS: Record<string, Filter> = {
  isActive: {
    id: 'isActive',
    name: 'isActive',
    label: 'Resource Status',
    type: 'toggle',
    chipLabel: [{ label: 'Active Resources', value: 'true' }],
    buttons: [{ value: 'true', label: 'Show Only Active' }],
  },
  employmentType: {
    id: 'employmentType',
    name: 'employmentType',
    label: 'Employment Type',
    type: 'select',
    conditionalRender: {
      parentEquals: 'contingent',
      id: 'contingentCompanies',
      name: 'contingentCompanies',
      label: 'Company',
      type: 'select',
      multi: true,
      required: true,
      options: [
        { value: 'pilot', title: 'Pilot' },
        { value: 'crawford', title: 'Crawford' },
        { value: 'other', title: 'Other' },
      ],
    },
    options: [
      { value: 'staff', title: 'Staff' },
      { value: 'contingent', title: 'Contingent' },
    ],
  },
};

/**
 * Hook to manage all scheduler filter definitions and async filter state
 *
 * Extracts filter logic from SchedulerPage to reduce component complexity.
 * Handles:
 * - Async filter input state and debouncing
 * - Dynamic filter options based on API data
 * - Task and resource filter definitions
 * - Quick filter options
 */
export function useSchedulerFilters({
  filterOptionsLookup,
  companyAttributes,
  taskAttributeOptions,
  isAdjuster = false,
  unassignedTasksCount = 0,
  qualifiedResourcesFnol = false,
}: UseSchedulerFiltersOptions) {
  // ====================
  // Async filter input state
  // ====================
  const [resourceFilterInput, setResourceFilterInput] = useState('');
  const [resourceFilterDebouncedValue, setResourceFilterDebouncedValue] =
    useState('');
  const [lossCountyFilterInput, setLossCountyFilterInput] = useState('');
  const [lossCountyFilterDebouncedValue, setLossCountyFilterDebouncedValue] =
    useState('');
  const [lossStateFilterInput, setLossStateFilterInput] = useState<string[]>(
    []
  );
  const [rsrcCountyFilterInput, setRsrcCountyFilterInput] = useState('');
  const [rsrcCountyFilterDebouncedValue, setRsrcCountyFilterDebouncedValue] =
    useState('');
  const [rsrcStateFilterInput, setRsrcStateFilterInput] = useState<string[]>(
    []
  );

  // Debounce async filter inputs
  useDebounce(() => setResourceFilterDebouncedValue(resourceFilterInput), 500, [
    resourceFilterInput,
  ]);
  useDebounce(
    () => setLossCountyFilterDebouncedValue(lossCountyFilterInput),
    500,
    [lossCountyFilterInput]
  );
  useDebounce(
    () => setRsrcCountyFilterDebouncedValue(rsrcCountyFilterInput),
    500,
    [rsrcCountyFilterInput]
  );

  // ====================
  // Async filter data fetching
  // ====================
  const filteredResources = useGetResourcesPaginated({
    filters: {
      resourceName: resourceFilterDebouncedValue,
      isActive: true,
      pageSize: 25,
    },
    enabled: !!resourceFilterDebouncedValue,
  });

  const filteredLossCounties = useGetCountyOptionsWithState(
    lossCountyFilterDebouncedValue,
    !!lossCountyFilterDebouncedValue,
    lossStateFilterInput
  );

  const filteredRsrcCounties = useGetCountyOptionsWithState(
    rsrcCountyFilterDebouncedValue,
    !!rsrcCountyFilterDebouncedValue,
    rsrcStateFilterInput
  );

  const mappedFilteredResources = useMemo(() => {
    if (filteredResources.status === 'success' && filteredResources.data) {
      return filteredResources.data.map(
        (resource): MultiSelectOption => ({
          title: resource.name,
          value: resource.integrationGUID,
        })
      );
    }
    return [];
  }, [filteredResources.data, filteredResources.status]);

  // ====================
  // Company attribute filters
  // ====================
  const companyAttributeFilters: Filter[] = useMemo(() => {
    return (
      companyAttributes?.companyAttributes?.map(
        (companyAttribute: AttributeOptions) => ({
          id: companyAttribute.title,
          name: companyAttribute.title,
          label: `Resource ${companyAttribute.title}`,
          multi: true,
          type: 'select' as const,
          options:
            companyAttribute.attributeDefinitionValues as MultiSelectOption[],
        })
      ) || []
    );
  }, [companyAttributes]);

  // ====================
  // Quick filter options
  // ====================
  const quickFilterOptions = useMemo(() => {
    let filters = [
      { label: 'All', value: 'all' },
      { label: 'Assigned', value: 'assigned' },
    ];

    if (!qualifiedResourcesFnol) {
      filters.push({
        label: 'Unassigned',
        value: 'unassigned',
        badgeCount: unassignedTasksCount,
      } as { label: string; value: string; badgeCount?: number });
    }

    if (isAdjuster) {
      filters = [{ label: 'All', value: 'all' }];
    }

    return filters;
  }, [isAdjuster, unassignedTasksCount, qualifiedResourcesFnol]);

  // ====================
  // Task filter definitions
  // ====================
  const taskFilterOptions: Filter[] = useMemo(
    () => [
      ...CORE_TASK_FILTERS,
      // Assigned resource filter (conditional on isAdjuster)
      ...(isAdjuster
        ? []
        : [
            {
              type: 'async-select' as const,
              id: 'assignedResourceIds',
              name: 'assignedResourceIds',
              label: 'Assigned Resource',
              multi: true,
              options: mappedFilteredResources,
              setInput: setResourceFilterInput,
              loading: filteredResources.status === 'loading',
              tagLimit: 10,
            },
          ]),
      // Loss states
      {
        id: 'lossStates',
        name: 'lossStates',
        label: 'Loss States',
        type: 'select' as const,
        multi: true,
        options: [
          { value: '', title: 'All' },
          ...(filterOptionsLookup.statesOptions || []),
        ],
      },
      // Loss counties (async)
      {
        id: 'lossCounties',
        name: 'lossCounties',
        label: 'Loss County',
        type: 'async-select' as const,
        multi: true,
        setInput: setLossCountyFilterInput,
        options: filteredLossCounties?.data || [],
        loading: filteredLossCounties?.status === 'loading',
      },
      // Loss zip codes
      STATIC_TASK_FILTERS['lossZipCodes'],
      // Offices
      {
        id: 'offices',
        name: 'offices',
        label: 'Offices',
        type: 'select' as const,
        multi: true,
        options: filterOptionsLookup.officeOptions || [],
      },
      // Groups
      {
        id: 'groups',
        name: 'groups',
        label: 'Groups',
        type: 'select' as const,
        multi: true,
        options: filterOptionsLookup.groupOptions || [],
      },
      // Loss regions
      {
        id: 'lossRegions',
        name: 'lossRegions',
        label: 'Regions',
        type: 'select' as const,
        multi: true,
        options: filterOptionsLookup.regionOptions || [],
      },
      // Perils
      {
        id: 'perils',
        name: 'perils',
        label: 'Perils',
        type: 'select' as const,
        multi: true,
        options: filterOptionsLookup.perilOptions || [],
      },
      // Method of inspection
      {
        id: 'methodsOfInspection',
        name: 'methodsOfInspection',
        label: 'Method Of Inspection',
        type: 'select' as const,
        multi: true,
        options: filterOptionsLookup.methodOfInspectionOptions || [],
      },
      {
        id: 'qualifiedResourceIds',
        name: 'qualifiedResourceIds',
        label: 'Qualified resources',
        type: 'async-select' as const,
        multi: true,
        options: [],
        tagLimit: 10,
        isVirtual: true,
      },
      // Task type
      {
        id: 'taskTypes',
        name: 'taskTypes',
        label: 'Task Type',
        type: 'select' as const,
        multi: true,
        options: [
          { value: 'CLAIM_OWNER', title: 'Claim Owner' },
          { value: 'NON_COMPLEX', title: 'Non Complex' },
        ],
      },
      // Task attributes
      {
        id: 'claimAttributes',
        name: 'claimAttributes',
        label: 'Task Attributes',
        type: 'select' as const,
        multi: true,
        options: (taskAttributeOptions || []) as MultiSelectOption[],
      },
    ],
    [
      filterOptionsLookup,
      mappedFilteredResources,
      filteredResources.status,
      filteredLossCounties,
      isAdjuster,
      taskAttributeOptions,
    ]
  );

  // ====================
  // Resource filter definitions
  // ====================
  const resourceFilterOptions: Filter[] = useMemo(
    () => [
      // isActive (static)
      STATIC_RESOURCE_FILTERS['isActive'],
      // Resource states
      {
        id: 'resourceStates',
        name: 'resourceStates',
        label: 'State',
        type: 'select' as const,
        multi: true,
        options: [
          { value: '', title: 'All' },
          ...(filterOptionsLookup.statesOptions || []),
        ],
      },
      // Resource counties (async)
      {
        id: 'resourceCounties',
        name: 'resourceCounties',
        label: 'Counties',
        type: 'async-select' as const,
        multi: true,
        setInput: setRsrcCountyFilterInput,
        options: filteredRsrcCounties?.data || [],
        loading: filteredRsrcCounties?.status === 'loading',
      },
      // Employment type (static)
      STATIC_RESOURCE_FILTERS['employmentType'],
      // Roles
      {
        id: 'roles',
        name: 'roles',
        label: 'Roles',
        type: 'select' as const,
        multi: true,
        options: filterOptionsLookup.companyRoles || [],
      },
      // Teams — shown in Resource Filters panel but stored under `taskFilters`
      // via the per-filter `contextKey` override. This keeps the context shape
      // identical to Task List (which also stores `teams` under taskFilters),
      // so saved filters and cross-nav state round-trip cleanly without needing
      // migration logic in DynamicFilterModal. Matches v1 behavior where the
      // same `filters.teams` key was read by both the task list and scheduler
      // API paths.
      {
        id: 'teams',
        name: 'teams',
        label: 'Teams',
        type: 'select' as const,
        multi: true,
        contextKey: 'taskFilters',
        options: filterOptionsLookup.teamOptions || [],
      },
      {
        id: 'resourceSelectedAttributeGroups',
        name: 'resourceSelectedAttributeGroups',
        label: 'Attribute Groups',
        type: 'select' as const,
        multi: true,
        options: filterOptionsLookup.attributeGroupOptions || [],
        persistAsArray: true,
      },
      // Company attribute filters
      ...companyAttributeFilters,
    ],
    [filterOptionsLookup, filteredRsrcCounties, companyAttributeFilters]
  );

  // ====================
  // Combined dynamic filters
  // ====================
  const dynamicFilters = useMemo(
    () => [
      {
        label: 'Task Filters',
        data: taskFilterOptions,
        contextKey: 'taskFilters',
      },
      {
        label: 'Resource Filters',
        data: resourceFilterOptions,
        contextKey: 'resourceFilters',
      },
    ],
    [taskFilterOptions, resourceFilterOptions]
  );

  return {
    // Filter definitions
    taskFilterOptions,
    resourceFilterOptions,
    dynamicFilters,
    quickFilterOptions,
    companyAttributeFilters,

    // State setters for DynamicFilterModal
    setLossStateFilterInput,
    setRsrcStateFilterInput,
  };
}
